import { useState } from "react";

import { makePostApiCall } from "../../../shared/makeApiCall";

function locationGroupKey(
  productId,
  search,
  locationId,
  zoneId,
  productTypeId,
) {
  return `${productId}|${search || ""}|${locationId || ""}|${zoneId || ""}|${productTypeId || ""}`;
}

function scopeKey(value) {
  return value == null || value === "" ? "none" : String(value);
}

function zoneGroupKey(productId, locationId, search, zoneId, productTypeId) {
  return `${productId}|${scopeKey(locationId)}|${search || ""}|${zoneId || ""}|${productTypeId || ""}`;
}

function lotGroupKey(productId, locationId, zoneId, search, productTypeId) {
  return `${productId}|${scopeKey(locationId)}|${scopeKey(zoneId)}|${search || ""}|${productTypeId || ""}`;
}

function cartonKey(
  productId,
  locationId,
  zoneId,
  lotNumber,
  search,
  productTypeId,
) {
  return `${productId}|${scopeKey(locationId)}|${scopeKey(zoneId)}|${scopeKey(lotNumber)}|${search || ""}|${productTypeId || ""}`;
}

export function InventoryManufacturedMaterialViewModel(logout) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({ data: [] });
  const [subRowDetails, setSubRowDetails] = useState({});
  const [zoneDetails, setZoneDetails] = useState({});
  const [lotDetails, setLotDetails] = useState({});
  const [cartonDetails, setCartonDetails] = useState({});
  const [loadingRows, setLoadingRows] = useState({});
  const [loadingZones, setLoadingZones] = useState({});
  const [loadingLots, setLoadingLots] = useState({});
  const [loadingCartons, setLoadingCartons] = useState({});

  const getInStockRawMaterial = async function (
    search,
    page,
    locationId,
    zoneId,
    productTypeId,
  ) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      location_id: locationId || null,
      zone_id: zoneId || null,
      product_type_id: productTypeId || null,
    };
    let callResult = await makePostApiCall(
      "inventoryManufacturedMaterial",
      payload,
    );

    if (callResult.success) {
      setProducts(callResult.data);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
    setLoading(false);
  };

  const fetchSubRowDetails = async function (
    rowData,
    search,
    locationId,
    zoneId,
    productTypeId,
  ) {
    const key = locationGroupKey(
      rowData.product_id,
      search,
      locationId,
      zoneId,
      productTypeId,
    );
    const payload = {
      product_id: rowData.product_id,
      search: search,
      location_id: locationId || null,
      zone_id: zoneId || null,
      product_type_id: productTypeId || null,
    };

    setLoadingRows((prev) => ({
      ...prev,
      [key]: true,
    }));

    let callResult = await makePostApiCall(
      "inventoryManufacturedLocations",
      payload,
    );

    setLoadingRows((prev) => ({
      ...prev,
      [key]: false,
    }));

    if (callResult.success) {
      setSubRowDetails((prev) => ({
        ...prev,
        [key]: callResult.data,
      }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchZones = async function (group, search, zoneId, productTypeId) {
    const key = zoneGroupKey(
      group.product_id,
      group.location_id,
      search,
      zoneId,
      productTypeId,
    );
    const payload = {
      product_id: group.product_id,
      location_id: group.location_id ?? null,
      search: search,
      zone_id: zoneId || null,
      product_type_id: productTypeId || null,
    };

    setLoadingZones((prev) => ({
      ...prev,
      [key]: true,
    }));

    let callResult = await makePostApiCall(
      "inventoryManufacturedZones",
      payload,
    );

    setLoadingZones((prev) => ({
      ...prev,
      [key]: false,
    }));

    if (callResult.success) {
      setZoneDetails((prev) => ({
        ...prev,
        [key]: callResult.data,
      }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchLots = async function (group, search, productTypeId) {
    const key = lotGroupKey(
      group.product_id,
      group.location_id,
      group.zone_id,
      search,
      productTypeId,
    );
    const payload = {
      product_id: group.product_id,
      location_id: group.location_id ?? null,
      zone_id: group.zone_id ?? null,
      search: search,
      product_type_id: productTypeId || null,
    };

    setLoadingLots((prev) => ({
      ...prev,
      [key]: true,
    }));

    let callResult = await makePostApiCall(
      "inventoryManufacturedLots",
      payload,
    );

    setLoadingLots((prev) => ({
      ...prev,
      [key]: false,
    }));

    if (callResult.success) {
      setLotDetails((prev) => ({
        ...prev,
        [key]: callResult.data,
      }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchCartons = async function (group, search, productTypeId) {
    const key = cartonKey(
      group.product_id,
      group.location_id,
      group.zone_id,
      group.lot_number,
      search,
      productTypeId,
    );
    const payload = {
      product_id: group.product_id,
      location_id: group.location_id ?? null,
      zone_id: group.zone_id ?? null,
      lot_number: group.lot_number ?? null,
      search: search,
      product_type_id: productTypeId || null,
    };

    setLoadingCartons((prev) => ({
      ...prev,
      [key]: true,
    }));

    let callResult = await makePostApiCall(
      "inventoryManufacturedCartons",
      payload,
    );

    setLoadingCartons((prev) => ({
      ...prev,
      [key]: false,
    }));

    if (callResult.success) {
      setCartonDetails((prev) => ({
        ...prev,
        [key]: callResult.data,
      }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const exportNetsuiteInventoryAdjustmentCsv = async function () {
    let callResult = await makePostApiCall(
      `exportNetsuiteInventoryAdjustmentCsv`,
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  return {
    getInStockRawMaterial,
    loading,
    products,
    exportNetsuiteInventoryAdjustmentCsv,
    fetchSubRowDetails,
    fetchZones,
    fetchLots,
    fetchCartons,
    loadingRows,
    loadingZones,
    loadingLots,
    loadingCartons,
    subRowDetails,
    zoneDetails,
    lotDetails,
    cartonDetails,
    locationGroupKey,
    zoneGroupKey,
    lotGroupKey,
    cartonKey,
  };
}
